/**
 * Die Frage an den Server: "Ist die Tuer zu, und reicht mein Code?"
 * (Produktentscheidung vom 04.10.2026).
 *
 * Gefragt wird einmal beim Laden der Seite, bevor das Menue erscheint. Nur so
 * kann die Infoseite zum geschlossenen Playtest VOR der Heldenauswahl stehen,
 * statt als Absage dahinter.
 *
 * Der Client raet dabei nichts: Ob ein Code noetig ist, weiss allein der
 * Server. Eine fest eingebaute Annahme waere irgendwann falsch - und dann
 * stuende entweder eine Codeabfrage vor einem offenen Server oder gar keine
 * vor einem geschlossenen.
 */

import { resolveServerUrl } from './MatchConnection.js';

/** Was der Server ueber die Tuer sagt. */
export interface TorAntwort {
  /** Braucht man ueberhaupt einen Code? */
  geschlossen: boolean;
  /** Reicht der mitgeschickte Code? */
  erlaubt: boolean;
}

/**
 * Macht aus der Spieladresse die Adresse der Auskunft.
 *
 * Beides laeuft auf demselben Port; nur das Protokoll unterscheidet sich,
 * weil das Match ueber WebSocket spricht und die Auskunft ueber HTTP.
 * Bewusst ohne regulaeren Ausdruck - die Umschrift ist so leichter zu lesen.
 */
export function torAdresse(serverUrl: string = resolveServerUrl()): string {
  const ohneProtokoll = serverUrl.startsWith('wss:')
    ? 'https:' + serverUrl.slice('wss:'.length)
    : serverUrl.startsWith('ws:')
      ? 'http:' + serverUrl.slice('ws:'.length)
      : serverUrl;
  const ohneSchraegstrich = ohneProtokoll.endsWith('/')
    ? ohneProtokoll.slice(0, -1)
    : ohneProtokoll;
  return ohneSchraegstrich + '/zugang';
}

/**
 * Fragt den Server. Gibt `null` zurueck, wenn er nicht erreichbar ist.
 *
 * `null` heisst ausdruecklich NICHT "offen": Der Client laesst den Spieler
 * dann zwar ins Menue, aber die Tuer steht unveraendert in `onAuth` auf dem
 * Server. Ohne diesen Rueckfall wuerde ein kurzer Aussetzer die ganze Seite
 * blockieren - und dem Spieler eine Codeabfrage zeigen, die gerade niemand
 * beantworten koennte.
 */
export async function frageZugang(code: string, signal?: AbortSignal): Promise<TorAntwort | null> {
  try {
    const antwort = await fetch(torAdresse(), {
      method: 'POST',
      // Bewusst "text/plain": Damit stellt der Browser keine Vorab-Anfrage.
      headers: { 'content-type': 'text/plain' },
      body: JSON.stringify({ code }),
      signal,
    });
    if (!antwort.ok) return null;

    const gelesen: unknown = await antwort.json();
    if (!gelesen || typeof gelesen !== 'object') return null;

    const roh = gelesen as { geschlossen?: unknown; erlaubt?: unknown };
    return {
      geschlossen: roh.geschlossen === true,
      erlaubt: roh.erlaubt === true,
    };
  } catch {
    // Server nicht erreichbar, Abbruch oder kaputte Antwort - siehe oben.
    return null;
  }
}

/**
 * Wohin der Knopf "Apply for the playtest" fuehrt.
 *
 * Standard ist die oeffentliche Homepage. Lokal laesst sich mit
 * VITE_PLAYTEST_URL auf die eigene Fassung zeigen, z. B.
 * http://127.0.0.1:5274/playtest.
 */
export function anmeldeAdresse(): string {
  const eingestellt = import.meta.env.VITE_PLAYTEST_URL;
  if (eingestellt && eingestellt.trim() !== '') return eingestellt.trim();
  return 'https://dueline-game.com/playtest';
}
